import type { JSONContent } from '@sequencehq/core-components'
import type {
  ListBlockConfig,
  ListBlockConfigV2,
  ListBlockConfigV3,
  QuoteBlock
} from '@sequencehq/quote-content'
import { match } from 'ts-pattern'
import { textContentAdapter } from './textContent'
import {
  textBlockAttributesIn,
  textBlockAttributesOut
} from './textBlockAttributes'

type ListItemConfig = ListBlockConfigV3['items'][number]

type ListEntry = { item: ListItemConfig | undefined; children: QuoteBlock[] }

/**
 * Converts between saved list blocks and the editor's nested lists.
 *
 * Each saved block is a single item, with any nested blocks as its `children`.
 * Older blocks (v1, v2, and v3 with several items) held a whole list, and are
 * split into items on load. Neighbouring items of the same type make up one
 * list in the editor, so numbering carries on from item to item.
 *
 *   numberList { items: [Kick-off], startsFrom: 1 }       orderedList (start 1)
 *   numberList { items: [Go live], startsFrom: 2 }   <->    1. Kick-off
 *                                                           2. Go live
 *
 * The nested blocks are converted by the caller, as they can be any block.
 */
export const listsAdapter = {
  in: (
    block: QuoteBlock,
    blocksIn: (blocks: QuoteBlock[]) => JSONContent[]
  ): JSONContent => {
    const isNumberList = block.blockType === 'numberList'

    return {
      type: isNumberList ? 'orderedList' : 'bulletList',
      ...(isNumberList && { attrs: { start: startsFrom(block) } }),
      content: listEntries(block).map(({ item, children }) => ({
        type: 'listItem',
        content: [
          {
            type: 'paragraph',
            attrs: textBlockAttributesIn(item),
            content: textContentAdapter.in(item?.text ?? [])
          },
          ...blocksIn(children)
        ]
      }))
    }
  },
  /**
   * Every item is saved numbered on from the one before it.
   */
  out: (
    list: JSONContent,
    blocksOut: (nodes: JSONContent[]) => QuoteBlock[]
  ): QuoteBlock[] => {
    const isNumberList = list.type === 'orderedList'
    const start: number = list.attrs?.start ?? 1

    return (list.content ?? []).map((listItem, index) => {
      const [paragraph, ...children] = listItem.content ?? []
      const { textAlignment, textColor, backgroundColor } =
        textBlockAttributesOut(paragraph?.attrs)

      return {
        blockType: isNumberList ? 'numberList' : 'bulletList',
        config: {
          items: [
            {
              text: textContentAdapter.out(paragraph?.content),
              backgroundColor,
              textColor,
              textAlignment
            }
          ],
          children: blocksOut(children),
          ...(isNumberList && { startsFrom: start + index })
        },
        version: 3
      }
    })
  }
}

/**
 * Lists of the same type next to each other become one list, as the editor
 * would join them anyway. The first list's start number wins.
 */
export const joinAdjacentLists = (nodes: JSONContent[]): JSONContent[] =>
  nodes.reduce<JSONContent[]>((acc, node) => {
    const previous = acc.at(-1)
    const isList = node.type === 'bulletList' || node.type === 'orderedList'
    const isSameList = isList && previous?.type === node.type

    if (!previous || !isSameList) {
      return [...acc, node]
    }

    return [
      ...acc.slice(0, -1),
      {
        ...previous,
        content: [...(previous.content ?? []), ...(node.content ?? [])]
      }
    ]
  }, [])

const listEntries = (block: QuoteBlock): ListEntry[] =>
  match(block.version)
    .with(3, () => {
      const config = block.config as ListBlockConfigV3

      if (config.items.length > 1) {
        return config.items.map(item => ({ item, children: [] }))
      }

      return [
        {
          item: config.items.at(0),
          children: (config.children ?? []).filter(Boolean)
        }
      ]
    })
    .with(2, () =>
      (block.config as ListBlockConfigV2).items.map(text => ({
        item: { text },
        children: []
      }))
    )
    .otherwise(() =>
      (block.config as ListBlockConfig).items.map(text => ({
        item: { text },
        children: []
      }))
    )

const startsFrom = (block: QuoteBlock) =>
  block.version === 3
    ? ((block.config as ListBlockConfigV3).startsFrom ?? 1)
    : 1
